import React, { useState } from "react";
import ColorLegend from "../colorLegend/ColorLegend";
import Modal from "../../atoms/Modal";
import { eventTrack } from "../../../common/helpers/helpers";
import { ANALYTICS_CATEGORIES } from "../common/constants";
import QuestionCircleIcon from "../../icons/QuestionCircle";

const helpModal = () => {
  const [showHelpModal, setShowHelpModal] = useState(false);
  const displayHelpModal = () => {
    // raise event for tracking
    eventTrack(ANALYTICS_CATEGORIES.CARDS_UI, "Open Help Modal", "");
    setShowHelpModal(true);
  };
  const closeRuleModal = () => {
    setShowHelpModal(false);
  };

  const getContent = () => {
    if (__IS_COMPONENTS_PORTAL__) {
      return (
        <div className="tw-p-4 tw-container">
          <h1>VarSome Clinical Cards View</h1>
          <p>
            Clinical data previously presented in tabs, is now presented in
            summary card format below the Variant table, Each card shows a short
            summary of key information. When appropriate cards are coloured by
            “pathogenicity” to help highlight most salient information. Cards
            are grouped by general, frequency and pathogenicity information.
            Clicking on a card displays the full data available.
          </p>
          <p>
            <b>Important</b>: no information has been removed and pathogenicity
            classifications are not affected.
          </p>
          <h3 className="tw-mt-8">Configure card layout</h3>
          <p>
            The <b>“Old layout”</b> toggle button reverts you back to the
            previous clinical data layout. This will be available until the end
            of the year at which point we will no longer support this option.
            Selecting the <b>“gene”</b> or <b>“variant”</b> toggle button
            presents data cards that represent either the variant or the
            associated gene information.
          </p>
          <p>
            The <b>“Display Options”</b> wheel on the top right hand side of the
            cards section, allows you to configure the size layout of the cards
            to Compact View. This reduces the size of the cards by hiding
            summary data.
          </p>
          <h3 className="tw-mt-8">Card Colors</h3>
          <p>
            Cards are gray unless their data can be used to classify a variant’s
            pathogenicity. In these cases we color the cards as follows:
          </p>
          <ColorLegend />
          <p>
            Importantly these are{" "}
            <b>
              <i>purely indications</i>
            </b>
            , please refer to the Germline Variant Classification, CNV or
            Somatic Variant Classification components for the actual automated
            classification recommendation.
          </p>
          <h3 className="tw-mt-8">Disabled Cards</h3>
          <p>
            Data sources for which no data is available will be displayed as
            grayed out cards. You cannot click on them. This also allows you to
            quickly see the full range of data-sources that have been used for
            annotation.
          </p>
        </div>
      );
    }
    return (
      <div className="tw-p-4 tw-container">
        <h1>VarSome Cards View</h1>
        <p>
          As the amount of information on VarSome is constantly growing, we have
          changed how data is displayed, offering a summary view for each source
          of data.
        </p>
        <p>
          Each source of data is displayed in a “card”, with a short summary of
          the key information it contains. Some data are coloured by
          “pathogenicity” to help highlight possibly salient information.
        </p>
        <p>
          Clicking on a card will display the full data available to VarSome for
          that data source, allowing you to jump instantly to the relevant data.
        </p>
        <p>
          This change helps you focus on the most salient information, reduces
          the need for scrolling, can grow as more data-sources are added, and
          makes the webpage faster whilst also using less memory.
        </p>
        <p>
          <b>Important</b>: no information has been removed and pathogenicity
          classifications are not affected.
        </p>
        <h3 className="tw-mt-8">Configure card layout</h3>
        <p>
          By clicking on the “Display Options” wheel on the top right hand side,
          you can configure the layout of the cards to best suit your needs.
          These settings are stored in your browser.
        </p>
        <p>The following options are available:</p>
        <ol>
          <li>
            <b>View all Data</b> shows all the information for all of the data
            sources in a single huge page with scrolling. This view is slower to
            navigate and may use up more of your computer’s resources.
          </li>
          <li>
            <b>Compact View</b> reduces the size of the cards by hiding the
            summary data resulting in a more streamlined interface.
          </li>
          <li>
            <b>SideBar View</b> moves the cards summary to the side of the
            screen.
          </li>
        </ol>
        <p>
          The combination of these three options brings you back to VarSome’s
          original single-page view.
        </p>
        <h3 className="tw-mt-8">Card Colors</h3>
        <p>
          Cards are gray unless their data can be used to classify a variant’s
          pathogenicity. In these cases we color the cards as follows:
        </p>
        <ColorLegend />
        <p>
          Importantly these are{" "}
          <b>
            <i>purely indications</i>
          </b>
          , please refer to the Germline Variant Classification, CNV or Somatic
          Variant Classification components for the actual automated
          classification recommendation.
        </p>
        <h3 className="tw-mt-8">Navigation</h3>
        <p>
          Clicking on a card shows you the full information available in VarSome
          for that data source. If you need to scroll to view more information,
          the title of the select card will remain on screen. Clicking on “View
          All” will take you back to the top of the page and the card summary.
        </p>
        <h3 className="tw-mt-8">Disabled Cards</h3>
        <p>
          Data sources for which no data is available will be displayed as
          grayed out cards. You cannot click on them. This also allows you to
          quickly see the full range of data-sources that have been used for
          annotation.
        </p>
        <p>
          Cards for which the data requires additional licenses will also be
          grayed out, click{" "}
          <a href="https://landing.varsome.com/varsome-premium" target="_blank">
            here
          </a>{" "}
          for more information about VarSome Premium.
        </p>
      </div>
    );
  };

  return (
    <>
      <div className="tw-cursor-pointer">
        <div
          className={`tw-flex tw-items-center tw-justify-center tw-border-primary tw-rounded-full ${
            __IS_COMPONENTS_PORTAL__ ? "tw-ml-2" : "tw-mx-2"
          } wizard-HelpModalInitiator`}
          title="Help"
          onClick={displayHelpModal}
        >
          <QuestionCircleIcon size={14} style={{ fill: "rgb(23, 127, 255)" }} />
        </div>
      </div>
      <Modal
        isOpen={showHelpModal}
        customModalStyle={{ width: "900px" }}
        onRequestClose={() => closeRuleModal()}
        ContentRender={() => getContent()}
      />
    </>
  );
};

export default helpModal;
